import { FC, useState, useEffect } from "react";
import { Button, Modal } from "antd";

interface GenevaAddressData {
  adresse?: string;
  codepostal?: string;
  localite?: string;
  rue?: string;
  numero?: string;
  departement_canton?: string;
  pays?: string;
  [key: string]: any;
}

interface NewBienOffMarketProps {
  parcelleData?: {
    noParcelle: string;
    noCommune: string;
    commune: string;
    coordinate: [number, number];
    terrainAreaM2?: number | null;
    buildingType?: string | null;
    buildingAreaM2?: number | null;
  };
  onClose?: () => void;
}

const NewBienOffMarket: FC<NewBienOffMarketProps> = ({ parcelleData, onClose }) => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [genevaAddressData, setGenevaAddressData] = useState<GenevaAddressData | null>(null);
  const [quartierSectVilleRiveData, setQuartierSectVilleRiveData] = useState<{Quartier?: string, SectVille?: string, Rive?: string} | null>(null);

  const handleOpenModal = () => {
    setIsModalVisible(true);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    if (onClose) {
      onClose();
    }
  };

  // Fetch Geneva address data using the coordinates
  const fetchGenevaAddressData = async (coordinates: [number, number]) => {
    try {
      const [lon, lat] = coordinates;
      const baseUrl = 'https://app2.ge.ch/tergeoservices/rest/services/Hosted/GEO_ADRESSE_AGGLO/FeatureServer/0/query';
      
      const params = new URLSearchParams({
        'f': 'json',
        'outFields': '*',
        'returnGeometry': 'false',
        'geometry': JSON.stringify({
          "x": lon,
          "y": lat,
          "spatialReference": {"wkid": 4326}
        }),
        'geometryType': 'esriGeometryPoint',
        'inSR': '4326',
        'spatialRel': 'esriSpatialRelIntersects',
        'distance': '30',
        'units': 'esriSRUnit_Meter',
        'resultRecordCount': '1'
      });

      const response = await fetch(`${baseUrl}?${params.toString()}`);
      const data = await response.json();
      
      if (data.features && data.features.length > 0) {
        const addressData = data.features[0].attributes;
        setGenevaAddressData(addressData);
        console.log('Geneva address data:', addressData);
        return addressData;
      }
    } catch (error) {
      console.error('Error fetching Geneva address data:', error);
    }
    return null;
  };

  // Fetch Quartier, SectVille, and Rive data using the backend endpoint
  const fetchQuartierSectVilleRiveData = async (parcelleData: any) => {
    try {
      const response = await fetch(
        `https://melcarnebackend.digipulse.ch/get_parcelle_address?commune_cadastre=${encodeURIComponent(parcelleData.commune)}&no_commune=${encodeURIComponent(parcelleData.noCommune)}&no_parcelle=${encodeURIComponent(parcelleData.noParcelle)}`
      );
      const data = await response.json();

      const quartierSectVilleRive = {
        Quartier: data.Quartier || undefined,
        SectVille: data.SectVille || undefined,
        Rive: data.Rive || undefined
      };
      
      setQuartierSectVilleRiveData(quartierSectVilleRive);
      console.log('Quartier, SectVille, and Rive data:', quartierSectVilleRive);
      return quartierSectVilleRive;
    } catch (error) {
      console.error('Error fetching Quartier, SectVille, and Rive data:', error);
    }
    return null;
  };

  // Fetch Geneva address data and Quartier/SectVille/Rive data when parcelleData is available
  useEffect(() => {
    if (parcelleData?.coordinate) {
      fetchGenevaAddressData(parcelleData.coordinate);
    }
    if (parcelleData) {
      fetchQuartierSectVilleRiveData(parcelleData);
    }
  }, [parcelleData]);

  // Automatically open modal if this component is rendered from context menu
  useEffect(() => {
    if (onClose && parcelleData) {
      setIsModalVisible(true);
    }
  }, [onClose, parcelleData]);

  // Listen for messages from iframe
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data.type === 'close-modal') {
        handleCancel();
      }
    };

    if (isModalVisible) {
      window.addEventListener('message', handleMessage);
      return () => window.removeEventListener('message', handleMessage);
    }
  }, [isModalVisible, handleCancel]);

  // Build URL with pre-filled data
  const buildEditCollectionUrl = () => {
    const baseUrl = "https://melcarne.digipulse.ch/collections/biens_off_market"; // directus-frontend default port
    const params = new URLSearchParams();
    
    // Add mode parameters
    params.append('mode', 'create');
    params.append('iframe', 'true'); // Indicate this is iframe mode
    
    if (parcelleData) {
      // Pre-fill parcelle information using the correct field names from the logged data
      params.append('NoParcelle', parcelleData.noParcelle);
      params.append('NoCommune', parcelleData.noCommune);
      
      // Add position data using the correct field name
      params.append('Position_sur_map', JSON.stringify({
        type: "Point",
        coordinates: parcelleData.coordinate
      }));
      
      // Pre-fill property data if available
      if (parcelleData.terrainAreaM2) {
        params.append('m2_terrain', parcelleData.terrainAreaM2.toString());
      }
      
      if (parcelleData.buildingType) {
        // Map building type to both Usage and Type_de_bien fields
        params.append('Usage', parcelleData.buildingType);
        params.append('Type_de_bien', parcelleData.buildingType);
      }
      
      if (parcelleData.buildingAreaM2) {
        params.append('m2_habitable', parcelleData.buildingAreaM2.toString());
      }
      
      // Add commune cadastre information
      if (parcelleData.commune) {
        params.append('CommuneCadastre', parcelleData.commune);
      }
      
      // Set default status using the correct value from the logged data
      params.append('status', 'actif');
    }

    // Add Geneva address data if available
    if (genevaAddressData) {
      // Use the actual field names from the Geneva API response
      if (genevaAddressData.rue && genevaAddressData.numero) {
        params.append('Adresse', genevaAddressData.rue + ' ' + genevaAddressData.numero);
      }
      if (genevaAddressData.codepostal) {
        params.append('NPA', genevaAddressData.codepostal);
      }
      if (genevaAddressData.localite) {
        params.append('CommuneAdresse', genevaAddressData.localite);
      }
    }

    // Add Quartier, SectVille, and Rive data if available
    if (quartierSectVilleRiveData) {
      if (quartierSectVilleRiveData.Quartier) {
        params.append('Quartier', quartierSectVilleRiveData.Quartier);
      }
      if (quartierSectVilleRiveData.SectVille) {
        params.append('SectVille', quartierSectVilleRiveData.SectVille);
      }
      if (quartierSectVilleRiveData.Rive) {
        params.append('Rive', quartierSectVilleRiveData.Rive);
      }
    }
    
    return `${baseUrl}?${params.toString()}`;
  };

  return (
    <>
      {!onClose && (
        <Button 
          type="primary" 
          onClick={handleOpenModal}
          disabled={!parcelleData}
        >
          Créer bien off-market
        </Button>
      )}

      <Modal
        title="Nouveau bien off-market"
        open={isModalVisible}
        onCancel={handleCancel}
        footer={null}
        width={1000}
        style={{ 
          top: 20,
          maxHeight: 'calc(100vh - 40px)', // Ensure modal doesn't exceed viewport
          paddingBottom: 0
        }}
        styles={{
          body: {
            height: 'calc(100vh - 160px)', // Account for header and padding
            padding: 0,
            overflow: 'hidden'
          }
        }}
        destroyOnClose={true}
        mask={false} // Prevent modal from moving when scrolling
      >
        <iframe
          src={buildEditCollectionUrl()}
          style={{
            width: '100%',
            height: '100%',
            border: 'none',
            borderRadius: '4px'
          }}
          title="Créer bien off-market"
        />
      </Modal>
    </>
  );
};

export default NewBienOffMarket; 